/**
 * Bots (BotSvr) — GM4 (feature bots_manage).
 *  - Aba Dashboard: quantos on / cadastrados, Iniciar (canais x quantidade, 1 por canal a cada N s) /
 *    Desligar todos, seletor de mapa com contagem, mapa do mundo com os bots AO VIVO (heartbeat do bot)
 *    e lista lateral — a seleção destaca nos dois.
 *  - Aba Bots: contas botsvr* + 1º personagem (o que o bot loga); clicar abre equipamento/inventário
 *    do char (ItemsView — edição de slot GM4 via ItemSlotEditModal) e o poder de combate.
 * Posição no mapa: as 256 células do mundo ocupam o quadrado [37,475) do PNG de 512px (ver mapPos).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Bot, RefreshCw, Play, Power, X, Loader2, CheckSquare, Square, Zap, ChevronLeft, Circle, LayoutDashboard,
  MessageCircle, Navigation, MapPin, Send, Plus, Save, Trash2, Music, Store, Pencil, Megaphone,
} from 'lucide-react';
import {
  listBots, listOnlineBots, startBots, stopBots,
  listBotWhispers, sendBotWhisper, moveBots, teleportBots,
  setBotsMode, sendBotMegaphone, getBotShop, saveBotShop, getBotShopTitle, openBotShops, closeBotShops, startOneBot, stopOneBot,
  type BotAccountRow, type OnlineBotRow, type BotWhisperRow, type ItemRow,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';
import { useConfirm } from '@/shared/components/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { ItemsView, SlotIcon } from './ItemsView';
import { formatClass, NATIONS_PT } from './labels';

const WORLDS: { id: number; name: string }[] = [
  { id: 1, name: 'Tundra Infame' },
  { id: 2, name: 'Deserto Lamentação' },
  { id: 3, name: 'Floresta do Desespero' },
  { id: 4, name: 'Porto Lux' },
  { id: 5, name: 'Forte Ruína' },
  { id: 6, name: 'Região dos Lagos' },
  { id: 7, name: 'Santuário Profano' },
  { id: 8, name: 'Elo Perdido' },
  { id: 9, name: 'Floresta Mutante' },
  { id: 10, name: 'Pontus Ferrum' },
  { id: 11, name: 'Portal Infernus' },
  { id: 12, name: 'Vestígio Arcano' },
  { id: 22, name: 'Senillinea' },
];
const CHANNELS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12];
const STATE_SHOP = 6;   // heartbeat do bot: lojinha aberta
const STATE_LABEL: Record<number, string> = { 1: 'logando', 2: 'no mundo', 3: 'andando', 4: 'dançando', 5: 'relogando', 6: 'em lojinha' };
const POLL_MS = 5000;
// Área jogável dentro do PNG (512px, textura do client): o quadrado [37,475) — a borda em volta é
// enfeite desenhado fora do mundo. As 256 células do mundo cobrem esses 438px; y cresce pra cima
// (client CMapDialog::GetMapPos: left + x*RATIO/256, bottom - y*RATIO/256).
const MAP_TEX = 512, MAP_LEFT = 37, MAP_SPAN = 438;
const mapPos = (x: number, y: number) => ({
  left: `${((MAP_LEFT + (x / 256) * MAP_SPAN) / MAP_TEX) * 100}%`,
  top: `${((MAP_LEFT + MAP_SPAN - (y / 256) * MAP_SPAN) / MAP_TEX) * 100}%`,
});
// Inverso do mapPos: ponto clicado no mapa -> célula do mundo (null = fora da área jogável).
const cellFromClick = (e: React.MouseEvent<HTMLElement>) => {
  const r = e.currentTarget.getBoundingClientRect();
  const px = ((e.clientX - r.left) / r.width) * MAP_TEX, py = ((e.clientY - r.top) / r.height) * MAP_TEX;
  const x = Math.round(((px - MAP_LEFT) / MAP_SPAN) * 256), y = Math.round(((MAP_LEFT + MAP_SPAN - py) / MAP_SPAN) * 256);
  return x < 0 || x > 255 || y < 0 || y > 255 ? null : { x, y };
};

type PickMode = 'move' | 'teleport';
const ACTION_BTN = 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition cursor-pointer '
  + 'bg-[#00e1d7]/10 border-[#00e1d7]/30 text-[#00e1d7] hover:bg-[#00e1d7]/20';

const fmtUptime = (s: number) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  return h ? `${h}h${String(m).padStart(2, '0')}` : `${m}min`;
};
const errMsg = (e: any, d: string) => e?.detail || e?.message || d;

export default function BotsPage() {
  const { notify } = useToast();
  // Rotas (F5 mantém a tela): /admin/bots = dashboard | /admin/bots/list = lista | /admin/bots/<charIdx> = bot
  const navigate = useNavigate();
  const { cid } = useParams();
  const { pathname } = useLocation();
  const openCid = cid && /^\d+$/.test(cid) ? Number(cid) : null;
  const tab: 'dashboard' | 'bots' = openCid != null || pathname.endsWith('/list') ? 'bots' : 'dashboard';
  const editBot = (characterIdx: number) => navigate(`/admin/bots/${characterIdx}`);
  const [accounts, setAccounts] = useState<BotAccountRow[]>([]);
  const openAcc = openCid != null ? accounts.find(a => a.characterIdx === openCid) ?? null : null;
  const [online, setOnline] = useState<OnlineBotRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAccounts = useCallback(async () => {
    try { setAccounts(await listBots()); } catch (e) { notify('error', errMsg(e, 'Erro ao carregar os bots')); }
  }, [notify]);
  const fetchOnline = useCallback(async () => {
    try { setOnline(await listOnlineBots()); } catch { /* poll silencioso */ }
  }, []);
  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchAccounts(), fetchOnline()]);
    setLoading(false);
  }, [fetchAccounts, fetchOnline]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const t = setInterval(fetchOnline, POLL_MS);
    return () => clearInterval(t);
  }, [fetchOnline]);

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Bot className="text-[#00e1d7]" size={26} /> Bots
          </h1>
          <p className="text-sm text-white/65 mt-1">BotSvr V2 — contas botsvr*, posição ao vivo e controle</p>
        </div>
        <button onClick={refresh} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7] hover:bg-[#00e1d7]/20 transition cursor-pointer disabled:opacity-50">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Atualizar
        </button>
      </div>

      {/* Tabs — mesmo padrão do Guard */}
      <div className="flex flex-wrap gap-1 mb-5 surface-card rounded-xl p-1 w-fit max-w-full">
        <TabBtn active={tab === 'dashboard'} onClick={() => navigate('/admin/bots')}>
          <LayoutDashboard size={14} /> Dashboard
        </TabBtn>
        <TabBtn active={tab === 'bots'} onClick={() => navigate('/admin/bots/list')}>
          <Bot size={14} /> Bots
        </TabBtn>
      </div>

      {tab === 'dashboard'
        ? <DashboardTab accounts={accounts} online={online} onChanged={refresh} onEdit={editBot} />
        : openCid != null && !openAcc
          ? (loading ? <div className="py-10 flex justify-center text-white/40"><Loader2 size={18} className="animate-spin" /></div>
                     : <p className="text-sm text-white/50 py-10 text-center">Bot {openCid} não encontrado.</p>)
          : <BotsTab accounts={accounts} online={online} loading={loading} open={openAcc}
                     setOpen={(a) => navigate(a?.characterIdx ? `/admin/bots/${a.characterIdx}` : '/admin/bots/list')}
                     onChanged={refresh} />}
    </div>
  );
}

/* ───────────────────────── Dashboard ───────────────────────── */

function DashboardTab({ accounts, online, onChanged, onEdit }: {
  accounts: BotAccountRow[]; online: OnlineBotRow[]; onChanged: () => void; onEdit: (characterIdx: number) => void;
}) {
  const { notify } = useToast();
  const confirm = useConfirm();
  const [channel, setChannel] = useState(1);
  const [world, setWorld] = useState(1);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [hover, setHover] = useState<number | null>(null);
  const [startOpen, setStartOpen] = useState(false);
  const [stopping, setStopping] = useState(false);
  // Mover/Teleportar: guarda os bots no início (trocar de mapa no teleporte limpa a seleção, não o alvo)
  const [pick, setPick] = useState<{ mode: PickMode; ids: number[]; fromWorld: number } | null>(null);
  const [cursorCell, setCursorCell] = useState<{ x: number; y: number } | null>(null);
  const [acting, setActing] = useState(false);
  const [whisperFor, setWhisperFor] = useState<OnlineBotRow | null>(null);
  const [megaFor, setMegaFor] = useState<OnlineBotRow | null>(null);
  const [modeOpen, setModeOpen] = useState(false);

  const registered = accounts.filter(a => a.characterIdx).length;
  // o mapa é por CANAL + mapa — nunca mistura canais
  const countByChannel = useMemo(() => {
    const m: Record<number, number> = {};
    for (const b of online) m[b.channel] = (m[b.channel] || 0) + 1;
    return m;
  }, [online]);
  const countByWorld = useMemo(() => {
    const m: Record<number, number> = {};
    for (const b of online) if (b.channel === channel) m[b.mapId] = (m[b.mapId] || 0) + 1;
    return m;
  }, [online, channel]);
  const inWorld = useMemo(
    () => online.filter(b => b.channel === channel && b.mapId === world).sort((a, b) => a.name.localeCompare(b.name)),
    [online, channel, world]);

  // Canais e mapas em ordem de quem tem MAIS bots online (empate = ordem normal)
  const sortedChannels = useMemo(
    () => [...CHANNELS].sort((a, b) => (countByChannel[b] || 0) - (countByChannel[a] || 0) || a - b),
    [countByChannel]);
  const topWorldOf = useCallback((ch: number) => {
    const m: Record<number, number> = {};
    for (const b of online) if (b.channel === ch) m[b.mapId] = (m[b.mapId] || 0) + 1;
    let best = 0, bestN = 0;
    for (const w of WORLDS) if ((m[w.id] || 0) > bestN) { best = w.id; bestN = m[w.id]; }
    return best || null;
  }, [online]);
  const sortedWorlds = useMemo(
    () => WORLDS.map((w, i) => ({ w, i })).sort((a, b) => (countByWorld[b.w.id] || 0) - (countByWorld[a.w.id] || 0) || a.i - b.i)
      .map(x => x.w),
    [countByWorld]);

  // Troca de canal já seleciona o mapa com mais bots dele
  const selectChannel = (c: number) => {
    setChannel(c);
    const w = topWorldOf(c);
    if (w) setWorld(w);
  };
  // Ao abrir: já vem no canal com mais bots e no mapa com mais bots dele (só uma vez)
  const autoPicked = useRef(false);
  useEffect(() => {
    if (autoPicked.current || online.length === 0) return;
    autoPicked.current = true;
    selectChannel(sortedChannels[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online.length, sortedChannels]);

  // seleção só vale pro canal+mapa atual; some quem saiu dele
  useEffect(() => { setSelected(new Set()); }, [channel, world]);
  useEffect(() => {
    setSelected(prev => {
      const ids = new Set(inWorld.map(b => b.characterIdx));
      const next = new Set([...prev].filter(id => ids.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [inWorld]);

  const toggle = (id: number) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  // "Selecionar todos" pula quem está em lojinha (só aceita sussurro/megafone/fechar)
  const selectable = inWorld.filter(b => b.state !== STATE_SHOP);
  const allSelected = selectable.length > 0 && selectable.every(b => selected.has(b.characterIdx));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map(b => b.characterIdx)));
  const selectedBots = inWorld.filter(b => selected.has(b.characterIdx));

  // Mover só vale no mapa atual dos bots; trocar de canal cancela qualquer um. Esc cancela.
  useEffect(() => { setPick(p => (p && p.mode === 'move' && world !== p.fromWorld ? null : p)); }, [world]);
  useEffect(() => { setPick(null); }, [channel]);
  useEffect(() => {
    if (!pick) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPick(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pick]);

  const startPick = (mode: PickMode) => setPick({ mode, ids: selectedBots.map(b => b.characterIdx), fromWorld: world });

  const applyMode = async (mode: 'afk' | 'dance') => {
    setModeOpen(false);
    try { notify('success', (await setBotsMode({ characterIdxs: selectedBots.map(b => b.characterIdx), mode })).message); }
    catch (e) { notify('error', errMsg(e, 'Erro ao mudar o modo')); }
  };

  // Lojinha (canais 1 e 12): abre com o Personal Shop configurado de cada bot / fecha.
  const openShop = async () => {
    try { notify('success', (await openBotShops(selectedBots.map(b => b.characterIdx))).message); }
    catch (e) { notify('error', errMsg(e, 'Erro ao abrir a lojinha')); }
  };
  const closeShop = async () => {
    try { notify('success', (await closeBotShops(selectedBots.map(b => b.characterIdx))).message); }
    catch (e) { notify('error', errMsg(e, 'Erro ao fechar a lojinha')); }
  };
  // Bot em lojinha só aceita Sussurros, Megafone e Fechar lojinha (andar com a loja aberta derruba o char)
  const anyInShop = selectedBots.some(b => b.state === STATE_SHOP);

  const runPick = async (cell: { x: number; y: number }) => {
    if (!pick || acting) return;
    setActing(true);
    try {
      const r = pick.mode === 'move'
        ? await moveBots({ characterIdxs: pick.ids, x: cell.x, y: cell.y })
        : await teleportBots({ characterIdxs: pick.ids, world, x: cell.x, y: cell.y });
      notify('success', r.message);
      setPick(null);
    } catch (e) { notify('error', errMsg(e, pick.mode === 'move' ? 'Erro ao mover' : 'Erro ao teleportar')); }
    finally { setActing(false); }
  };

  const doStop = async () => {
    const ok = await confirm({
      title: 'Desligar TODOS os bots?',
      description: 'Encerra os processos do BotSvr V2 (inclui o teste do canal 12). Os bots saem do jogo em alguns segundos.',
      tone: 'danger', confirmLabel: 'Desligar bots',
    });
    if (!ok) return;
    setStopping(true);
    try {
      const r = await stopBots();
      notify('success', r.message);
      setTimeout(onChanged, 4000);
    } catch (e) { notify('error', errMsg(e, 'Erro ao desligar')); }
    finally { setStopping(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-stretch gap-3 mb-4">
        <StatCard label="Bots on" value={online.length} accent="text-emerald-400" />
        <StatCard label="Bots cadastrados (em char)" value={registered} accent="text-[#00e1d7]" />
        <div className="ml-auto flex items-center gap-2">
          <button onClick={() => setStartOpen(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 transition cursor-pointer">
            <Play size={15} /> Iniciar bot
          </button>
          <button onClick={doStop} disabled={stopping}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition cursor-pointer disabled:opacity-50">
            {stopping ? <Loader2 size={15} className="animate-spin" /> : <Power size={15} />} Desligar bots
          </button>
        </div>
      </div>

      {/* seletor de canal */}
      <div className="surface-card rounded-xl p-2 mb-2 flex gap-1.5 overflow-x-auto">
        {sortedChannels.map(c => {
          const n = countByChannel[c] || 0;
          return (
            <button key={c} onClick={() => selectChannel(c)}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition border whitespace-nowrap
                ${channel === c ? 'bg-[#00e1d7]/15 text-[#00e1d7] border-[#00e1d7]/40'
                                : 'bg-white/[0.03] text-white/60 border-white/[0.06] hover:text-white'}`}>
              Canal {c} <span className={n ? 'text-emerald-400' : 'text-white/30'}>({n})</span>
            </button>
          );
        })}
      </div>

      {/* seletor de mapa (contagem do canal escolhido) */}
      <div className="surface-card rounded-xl p-2 mb-4 flex gap-1.5 overflow-x-auto">
        {sortedWorlds.map(w => {
          const n = countByWorld[w.id] || 0;
          return (
            <button key={w.id} onClick={() => setWorld(w.id)}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition border whitespace-nowrap
                ${world === w.id ? 'bg-[#00e1d7]/15 text-[#00e1d7] border-[#00e1d7]/40'
                                 : 'bg-white/[0.03] text-white/60 border-white/[0.06] hover:text-white'}`}>
              {w.name} <span className={n ? 'text-emerald-400' : 'text-white/30'}>({n})</span>
            </button>
          );
        })}
      </div>

      {/* mapa no tamanho real do PNG (512px) + folga; o resto da largura vai pra lista */}
      <div className="grid grid-cols-1 lg:grid-cols-[536px_minmax(0,1fr)] gap-4">
        {/* mapa */}
        <div className="surface-card rounded-xl p-3">
          <div className={`relative w-full max-w-[512px] mx-auto aspect-square rounded-lg overflow-hidden bg-black/40 select-none
                           ${pick ? 'cursor-crosshair ring-2 ring-amber-400/60' : ''}`}
               onMouseMove={pick ? (e) => setCursorCell(cellFromClick(e)) : undefined}
               onMouseLeave={() => setCursorCell(null)}
               onClick={pick ? (e) => { const c = cellFromClick(e); if (c) runPick(c); } : undefined}>
            <img src={`/maps/world${world}.png`} alt="" draggable={false} className="absolute inset-0 w-full h-full object-cover" />
            {pick && (
              <div className="absolute top-2 left-2 right-2 z-30 flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-black/85 border border-amber-400/40 text-[11px] text-white"
                   onClick={(e) => e.stopPropagation()}>
                {acting ? <Loader2 size={13} className="animate-spin text-amber-300" />
                        : pick.mode === 'move' ? <Navigation size={13} className="text-amber-300" /> : <MapPin size={13} className="text-amber-300" />}
                <span className="font-bold">
                  {pick.mode === 'move' ? 'Mover' : 'Teleportar'} {pick.ids.length} bot(s):
                </span>
                <span className="text-white/70 truncate">
                  {pick.mode === 'move' ? 'clique no destino' : 'escolha o mapa acima (opcional) e clique no destino'}
                  {cursorCell && ` · (${cursorCell.x},${cursorCell.y})`}
                </span>
                <button onClick={() => setPick(null)} className="ml-auto text-white/60 hover:text-white cursor-pointer" title="Cancelar (Esc)">
                  <X size={14} />
                </button>
              </div>
            )}
            {pick && cursorCell && (
              <span className="absolute w-3 h-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-amber-300 pointer-events-none z-20"
                    style={mapPos(cursorCell.x, cursorCell.y)} />
            )}
            {inWorld.map(b => {
              const sel = selected.has(b.characterIdx);
              const hov = hover === b.characterIdx;
              return (
                <button key={b.characterIdx}
                  onClick={() => toggle(b.characterIdx)}
                  onMouseEnter={() => setHover(b.characterIdx)} onMouseLeave={() => setHover(null)}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer ${pick ? 'pointer-events-none' : ''}`}
                  style={{ ...mapPos(b.x, b.y), zIndex: sel || hov ? 20 : 10 }}>
                  <img src={sel ? '/maps/me.png' : '/maps/party.png'} alt=""
                       className={sel ? 'w-6 h-6 drop-shadow-[0_0_4px_#00e1d7]' : 'w-3.5 h-3.5'} />
                  {(hov || sel) && (
                    <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1 px-1.5 py-0.5 rounded bg-black/85 border border-white/15 text-[10px] text-white whitespace-nowrap pointer-events-none">
                      {b.name} · ({b.x},{b.y})
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* lista */}
        <div className="surface-card rounded-xl p-3 flex flex-col min-h-0 lg:h-[536px]">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold text-white/80">{inWorld.length} online · Canal {channel}</p>
            <button onClick={toggleAll} disabled={!inWorld.length}
              className="flex items-center gap-1.5 text-[11px] font-bold text-[#00e1d7] hover:text-white cursor-pointer disabled:opacity-40">
              {allSelected ? <CheckSquare size={13} /> : <Square size={13} />} Selecionar todos
            </button>
          </div>
          {inWorld.length === 0 && <p className="text-xs text-white/40 py-6 text-center">Nenhum bot neste mapa</p>}
          <div className="flex-1 min-h-0 overflow-y-auto max-h-[480px] lg:max-h-none grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-1 content-start pr-1">
            {inWorld.map(b => {
              const sel = selected.has(b.characterIdx);
              const inShop = b.state === STATE_SHOP;
              return (
                <button key={b.characterIdx} onClick={() => toggle(b.characterIdx)}
                  onMouseEnter={() => setHover(b.characterIdx)} onMouseLeave={() => setHover(null)}
                  className={`w-full text-left px-2.5 py-1.5 rounded-lg border transition cursor-pointer
                    ${sel ? 'bg-[#00e1d7]/15 border-[#00e1d7]/40'
                          : inShop ? 'bg-amber-400/[0.07] border-amber-400/35'
                          : hover === b.characterIdx ? 'bg-white/[0.06] border-white/[0.1]' : 'bg-white/[0.02] border-white/[0.05]'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs font-bold truncate ${sel ? 'text-[#00e1d7]' : 'text-white'}`}>{b.name}</span>
                    {inShop && (
                      <span className="shrink-0 inline-flex items-center gap-1 px-1.5 py-px rounded bg-amber-400 text-black text-[10px] font-extrabold uppercase tracking-wide">
                        <Store size={10} /> Em lojinha
                      </span>
                    )}
                    {b.unread > 0 && (
                      <span className="shrink-0 inline-flex items-center gap-0.5 px-1.5 rounded-full bg-amber-400 text-black text-[10px] font-bold"
                            title={`${b.unread} sussurro(s) não lido(s)`}>
                        <MessageCircle size={9} /> {b.unread}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-white/40 flex justify-between">
                    <span>{STATE_LABEL[b.state] || `estado ${b.state}`} · ({b.x},{b.y})</span>
                    <span>{fmtUptime(b.uptime)}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {/* barra de ações — fixa no rodapé da lista enquanto houver bot selecionado */}
          {selectedBots.length > 0 && (
            <div className="mt-2 pt-2 border-t border-white/[0.08] flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-bold text-white/60 mr-auto">{selectedBots.length} selecionado(s)</span>
              {selectedBots.length === 1 && (
                <button onClick={() => setWhisperFor(selectedBots[0])}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-purple-500/10 border border-purple-500/30 text-purple-300 hover:bg-purple-500/20 transition cursor-pointer">
                  <MessageCircle size={13} /> Sussurros
                  {selectedBots[0].unread > 0 && (
                    <span className="px-1.5 rounded-full bg-amber-400 text-black text-[10px]">{selectedBots[0].unread}</span>
                  )}
                </button>
              )}
              {!anyInShop && (
                <>
                  <button onClick={() => startPick('move')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition cursor-pointer
                      ${pick?.mode === 'move' ? 'bg-amber-400/20 border-amber-400/50 text-amber-300'
                                              : 'bg-[#00e1d7]/10 border-[#00e1d7]/30 text-[#00e1d7] hover:bg-[#00e1d7]/20'}`}>
                    <Navigation size={13} /> Mover
                  </button>
                  <button onClick={() => startPick('teleport')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition cursor-pointer
                      ${pick?.mode === 'teleport' ? 'bg-amber-400/20 border-amber-400/50 text-amber-300'
                                                  : 'bg-[#00e1d7]/10 border-[#00e1d7]/30 text-[#00e1d7] hover:bg-[#00e1d7]/20'}`}>
                    <MapPin size={13} /> Teleportar
                  </button>
                  <button onClick={() => setModeOpen(true)} className={ACTION_BTN}>
                    <Music size={13} /> Modo
                  </button>
                  {(channel === 1 || channel === 12) && (
                    <button onClick={openShop} className={ACTION_BTN} title="Lojinha só nos canais 1 e 12">
                      <Store size={13} /> Abrir lojinha
                    </button>
                  )}
                </>
              )}
              {selectedBots.length === 1 && selectedBots[0].megaphone && (
                <button onClick={() => setMegaFor(selectedBots[0])} className={ACTION_BTN}>
                  <Megaphone size={13} /> Megafone
                </button>
              )}
              {anyInShop && (
                <button onClick={closeShop}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition cursor-pointer bg-amber-400/15 border-amber-400/40 text-amber-300 hover:bg-amber-400/25">
                  <Store size={13} /> Fechar lojinha
                </button>
              )}
              {!anyInShop && selectedBots.length === 1 && (
                <button onClick={() => onEdit(selectedBots[0].characterIdx)} className={ACTION_BTN}>
                  <Pencil size={13} /> Editar
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {whisperFor && <WhisperModal bot={whisperFor} onClose={() => setWhisperFor(null)} />}
      {megaFor && <MegaphoneModal bot={megaFor} onClose={() => setMegaFor(null)} />}
      {modeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
             onMouseDown={(e) => { if (e.target === e.currentTarget) setModeOpen(false); }}>
          <div className="surface-card rounded-2xl p-5 w-full max-w-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-white">Modo — {selectedBots.length} bot(s)</h3>
              <button onClick={() => setModeOpen(false)} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => applyMode('afk')}
                className="px-3 py-4 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white font-bold text-sm hover:border-[#00e1d7]/40 cursor-pointer">
                AFK<span className="block text-[10px] text-white/45 font-normal mt-1">fica parado</span>
              </button>
              <button onClick={() => applyMode('dance')}
                className="px-3 py-4 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white font-bold text-sm hover:border-[#00e1d7]/40 cursor-pointer">
                Dançando<span className="block text-[10px] text-white/45 font-normal mt-1">dança onde o jogo deixa</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <StartModal open={startOpen} onClose={() => setStartOpen(false)} initialChannel={channel}
        free={accounts.filter(a => a.characterIdx && !a.online && a.accountId !== 'botsvr1').length}
        freeShop={accounts.filter(a => a.characterIdx && !a.online && a.accountId !== 'botsvr1' && a.shopSlots > 0).length}
        onStarted={() => setTimeout(onChanged, 3000)} />
    </div>
  );
}

/* Sussurros do bot (memória do hub). Quem responde é a STAFF — o bot nunca responde sozinho.
   Clicar num recebido preenche o destino com quem mandou. Abrir marca os recebidos como lidos. */
function WhisperModal({ bot, onClose }: { bot: OnlineBotRow; onClose: () => void }) {
  const { notify } = useToast();
  const [rows, setRows] = useState<BotWhisperRow[]>([]);
  const [to, setTo] = useState('');
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try { setRows(await listBotWhispers(bot.characterIdx)); } catch { /* poll silencioso */ }
  }, [bot.characterIdx]);
  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);
  // destino padrão = último que mandou
  useEffect(() => {
    if (to) return;
    const last = [...rows].reverse().find(r => r.dir === 'in');
    if (last) setTo(last.name);
  }, [rows, to]);
  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight }); }, [rows.length]);

  const send = async () => {
    if (!to.trim() || !text.trim() || sending) return;
    setSending(true);
    try {
      const w = await sendBotWhisper(bot.characterIdx, { to: to.trim(), text: text.trim() });
      setRows(r => [...r, w]);
      setText('');
      textRef.current?.focus();
    } catch (e) { notify('error', errMsg(e, 'Erro ao enviar o sussurro')); }
    finally { setSending(false); }
  };

  const hhmm = (ts: number) => new Date(ts * 1000).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const input = 'bg-[#0a0e14] border border-white/[0.08] rounded-lg px-2.5 py-2 text-sm text-white outline-none focus:border-purple-400/50';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-5 w-full max-w-lg flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <MessageCircle size={16} className="text-purple-300" /> Sussurros
            <span className="text-white/45 font-normal">— {bot.name} · Canal {bot.channel}</span>
          </h3>
          <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
        </div>

        <div ref={listRef} className="flex-1 min-h-[220px] max-h-[420px] overflow-y-auto space-y-1.5 pr-1 mb-3">
          {rows.length === 0 && <p className="text-xs text-white/40 text-center py-10">Nenhum sussurro ainda</p>}
          {rows.map(r => r.dir === 'in' ? (
            <button key={r.id} onClick={() => { setTo(r.name); textRef.current?.focus(); }}
              title={`Responder a ${r.name}`}
              className="block w-[85%] text-left px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.06] hover:border-purple-400/40 cursor-pointer">
              <span className="text-[11px] font-bold text-purple-300">{r.name}</span>
              <span className="text-[10px] text-white/35 ml-2">{hhmm(r.ts)}</span>
              <p className="text-sm text-white/90 break-words">{r.text}</p>
            </button>
          ) : (
            <div key={r.id} className="ml-auto w-[85%] px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20">
              <span className="text-[11px] font-bold text-white/60">→ {r.name}</span>
              <span className="text-[10px] text-white/35 ml-2">{hhmm(r.ts)}</span>
              <p className="text-sm text-white/90 break-words">{r.text}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Personagem" maxLength={16}
                 className={`${input} w-36`} />
          <input ref={textRef} value={text} onChange={(e) => setText(e.target.value)} placeholder="Mensagem" maxLength={200}
                 onKeyDown={(e) => { if (e.key === 'Enter') send(); }} className={`${input} flex-1 min-w-0`} />
          <button onClick={send} disabled={sending || !to.trim() || !text.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-purple-500/15 border border-purple-500/35 text-purple-300 text-xs font-bold hover:bg-purple-500/25 cursor-pointer disabled:opacity-50">
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Enviar
          </button>
        </div>
        <p className="text-[10px] text-white/35 mt-2">O bot envia o sussurro dentro do jogo. Clique numa mensagem recebida para responder a quem mandou.</p>
      </div>
    </div>
  );
}

/* Megafone pelo bot (C2S 2089 — precisa do item Megafone; a API acha o slot dele no login do bot). */
function MegaphoneModal({ bot, onClose }: { bot: OnlineBotRow; onClose: () => void }) {
  const { notify } = useToast();
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      notify('success', (await sendBotMegaphone(bot.characterIdx, text.trim())).message);
      onClose();
    } catch (e) { notify('error', errMsg(e, 'Erro ao enviar o megafone')); }
    finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="surface-card rounded-2xl p-5 w-full max-w-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-white flex items-center gap-2">
            <Megaphone size={16} className="text-[#00e1d7]" /> Megafone
            <span className="text-white/45 font-normal">— {bot.name} · Canal {bot.channel}</span>
          </h3>
          <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
        </div>
        <div className="flex gap-2">
          <input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Mensagem" maxLength={100}
                 onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
                 className="flex-1 min-w-0 bg-[#0a0e14] border border-white/[0.08] rounded-lg px-2.5 py-2 text-sm text-white outline-none focus:border-[#00e1d7]/50" />
          <button onClick={send} disabled={sending || !text.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#00e1d7]/15 border border-[#00e1d7]/35 text-[#00e1d7] text-xs font-bold hover:bg-[#00e1d7]/25 cursor-pointer disabled:opacity-50">
            {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Enviar
          </button>
        </div>
        <p className="text-[10px] text-white/35 mt-2">Gasta 1 Megafone do inventário do bot.</p>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent }: { label: string; value: number; accent: string }) {
  return (
    <div className="surface-card rounded-xl px-5 py-3 min-w-[180px]">
      <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold">{label}</p>
      <p className={`text-3xl font-bold ${accent}`}>{value}</p>
    </div>
  );
}

// Canais que aceitam lojinha (espelha SHOP_CHANNELS da API)
const SHOP_CHANNELS = [1, 12];

function StartModal({ open, onClose, free, freeShop, onStarted, initialChannel }: {
  open: boolean; onClose: () => void; free: number; freeShop: number; onStarted: () => void; initialChannel: number;
}) {
  const { notify } = useToast();
  const [chans, setChans] = useState<Set<number>>(new Set());
  const [perChannel, setPerChannel] = useState(5);
  const [delaySec, setDelaySec] = useState(300);
  const [shopOnly, setShopOnly] = useState(false);
  const [busy, setBusy] = useState(false);

  // abre com o canal que está sendo visto no dashboard já marcado
  useEffect(() => { if (open) { setChans(new Set([initialChannel])); setShopOnly(false); } }, [open, initialChannel]);

  // Somente com Lojinha: só os canais de lojinha ficam habilitados (tira os outros da seleção)
  const toggleShopOnly = (on: boolean) => {
    setShopOnly(on);
    if (on) setChans(prev => new Set([...prev].filter(c => SHOP_CHANNELS.includes(c))));
  };

  if (!open) return null;
  const asked = chans.size * perChannel;
  // Somente com Lojinha: liga o máximo que tiver (a API corta no que existe)
  const total = shopOnly ? Math.min(asked, freeShop) : asked;
  const avail = shopOnly ? freeShop : free;
  // em ondas: 1 bot por canal agora, a próxima onda a cada delaySec
  const waves = chans.size ? Math.ceil(total / chans.size) : 0;
  const lastMin = Math.round(((waves - 1) * delaySec) / 60);

  const toggle = (c: number) => setChans(prev => {
    const n = new Set(prev);
    if (n.has(c)) n.delete(c); else n.add(c);
    return n;
  });

  const submit = async () => {
    if (!chans.size) { notify('error', 'Escolha ao menos 1 canal'); return; }
    setBusy(true);
    try {
      const r = await startBots({ channels: [...chans].sort((a, b) => a - b), perChannel, delaySec, shopOnly });
      notify('success', r.message);
      onStarted();
      onClose();
    } catch (e) { notify('error', errMsg(e, 'Erro ao iniciar')); }
    finally { setBusy(false); }
  };

  const num = 'w-full bg-[#0a0e14] border border-white/[0.06] rounded px-2 py-1.5 text-sm text-white font-mono';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
         onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="surface-card rounded-2xl p-5 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-white flex items-center gap-2"><Play size={16} className="text-emerald-400" /> Iniciar bots</h3>
          <button onClick={onClose} disabled={busy} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
        </div>

        <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
          <input type="checkbox" checked={shopOnly} onChange={(e) => toggleShopOnly(e.target.checked)}
                 className="w-4 h-4 accent-emerald-500 cursor-pointer" />
          <span className="text-xs font-bold text-white flex items-center gap-1.5"><Store size={13} className="text-[#00e1d7]" /> Somente com Lojinha</span>
          <span className="text-[11px] text-white/40">· {freeShop} bot(s) livre(s) com lojinha</span>
        </label>

        <p className="text-[11px] text-white/50 mb-1.5">
          {shopOnly ? 'Canais com lojinha (1 e 12)' : 'Canais (qualquer um de 1 a 10 e o 12)'}
        </p>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {CHANNELS.map(c => {
            const off = shopOnly && !SHOP_CHANNELS.includes(c);
            return (
              <button key={c} onClick={() => toggle(c)} disabled={off}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition
                  ${off ? 'opacity-30 cursor-not-allowed bg-white/[0.02] text-white/40 border-white/[0.04]'
                        : chans.has(c) ? 'cursor-pointer bg-emerald-500/15 text-emerald-400 border-emerald-500/40'
                                       : 'cursor-pointer bg-white/[0.03] text-white/60 border-white/[0.06] hover:text-white'}`}>
                Canal {c}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[11px] text-white/50">Quantos por canal</span>
            <input type="number" min={1} max={100} value={perChannel}
                   onChange={(e) => setPerChannel(Math.max(1, Math.min(100, Number(e.target.value) || 1)))} className={num} />
          </label>
          <label className="block">
            <span className="text-[11px] text-white/50">Intervalo (s) — 1 por canal</span>
            <input type="number" min={5} max={3600} value={delaySec}
                   onChange={(e) => setDelaySec(Math.max(5, Math.min(3600, Number(e.target.value) || 5)))} className={num} />
          </label>
        </div>

        <p className={`text-[11px] mt-3 leading-relaxed ${total > avail || (shopOnly && !freeShop) ? 'text-red-400' : 'text-white/45'}`}>
          {total} bot(s) no total · {avail} conta(s) livre(s){shopOnly ? ' com lojinha' : ''}
          {chans.size > 0 && total > 0 && ` · ${Math.min(total, chans.size)} agora (1 por canal)`}
          {chans.size > 0 && waves > 1 && `, depois mais ${chans.size} a cada ${delaySec}s (última onda em ~${lastMin} min)`}.
          {shopOnly && asked > freeShop && ` Pediu ${asked}, mas só ${freeShop} têm lojinha — liga o máximo que tiver.`}
          {' '}Modo AFK, nascem na Tundra.
        </p>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} disabled={busy}
            className="px-3 py-2 rounded-lg bg-white/[0.05] text-white/70 text-xs font-bold hover:bg-white/10 cursor-pointer">Cancelar</button>
          <button onClick={submit} disabled={busy || !chans.size || !total || total > avail}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500/15 border border-emerald-500/35 text-emerald-400 text-xs font-bold hover:bg-emerald-500/25 cursor-pointer disabled:opacity-50">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Iniciar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Bots ───────────────────────── */

function BotsTab({ accounts, online, loading, open, setOpen, onChanged }: {
  accounts: BotAccountRow[]; online: OnlineBotRow[]; loading: boolean;
  open: BotAccountRow | null; setOpen: (a: BotAccountRow | null) => void; onChanged: () => void;
}) {
  const live = useMemo(() => new Map(online.map(b => [b.characterIdx, b])), [online]);
  // ON = heartbeat do bot (V2). O Login do banco fica preso em 1 depois de pkill -> nao serve.
  const isOn = useCallback((a: BotAccountRow) => a.characterIdx != null && live.has(a.characterIdx), [live]);

  // Ordenação (padrão: online primeiro)
  const [sort, setSort] = useState<{ key: string; order: 'asc' | 'desc' }>({ key: 'status', order: 'desc' });
  const toggleSort = (key: string) =>
    setSort(s => (s.key === key ? { key, order: s.order === 'asc' ? 'desc' : 'asc' }
                                : { key, order: key === 'status' || key === 'power' || key === 'shop' ? 'desc' : 'asc' }));
  const sorted = useMemo(() => {
    const val = (a: BotAccountRow): number | string =>
      sort.key === 'account' ? a.userNum
      : sort.key === 'name' ? (a.name || '').toLowerCase()
      : sort.key === 'power' ? a.combatPower
      : sort.key === 'shop' ? a.shopSlots
      : isOn(a) ? 1 : 0;
    const dir = sort.order === 'asc' ? 1 : -1;
    return [...accounts].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (va < vb) return -dir;
      if (va > vb) return dir;
      return a.userNum - b.userNum;
    });
  }, [accounts, sort, isOn]);

  const cols: DataTableColumn<BotAccountRow>[] = [
    {
      key: 'account', label: 'Conta', width: '1.2fr', sortKey: 'account',
      cell: (a) => (
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate">{a.accountId}</p>
          <p className="text-[10px] text-white/35 font-mono">UserNum {a.userNum}</p>
        </div>
      ),
    },
    {
      key: 'name', label: '1º personagem', width: '1.6fr', sortKey: 'name',
      cell: (a) => (
        <div className="min-w-0">
          <p className="text-sm text-white truncate">{a.name || '—'}</p>
          <p className="text-[10px] text-white/40 truncate">{formatClass(a.charClass)} · Lv {a.level ?? '—'}</p>
        </div>
      ),
    },
    {
      key: 'power', label: 'Poder de combate', width: '140px', align: 'right', sortKey: 'power',
      cell: (a) => <span className="text-sm font-bold text-amber-300 font-mono">{a.combatPower.toLocaleString('pt-BR')}</span>,
    },
    {
      key: 'shop', label: 'Personal Shop', width: '120px', align: 'center', sortKey: 'shop',
      cell: (a) => a.shopSlots > 0
        ? <span className="inline-flex items-center gap-1 text-xs font-bold text-[#00e1d7]"><Store size={12} /> {a.shopSlots}/8</span>
        : <span className="text-xs text-white/30">—</span>,
    },
    {
      key: 'status', label: 'Status', width: '150px', sortKey: 'status',
      cell: (a) => {
        const b = a.characterIdx != null ? live.get(a.characterIdx) : undefined;
        return isOn(a)
          ? <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-400">
              <Circle size={8} className="fill-emerald-400" /> ON{b ? ` · CH${b.channel}` : ''}</span>
          : <span className="inline-flex items-center gap-1.5 text-xs font-bold text-white/35">
              <Circle size={8} /> OFF</span>;
      },
    },
  ];

  if (open) return <BotDetail acc={open} on={isOn(open)} live={open.characterIdx != null ? live.get(open.characterIdx) : undefined}
                              onBack={() => setOpen(null)} onChanged={onChanged} />;

  return (
    <DataTable<BotAccountRow>
      columns={cols}
      rows={sorted}
      rowKey={(a) => a.userNum}
      onRowClick={(a) => a.characterIdx && setOpen(a)}
      loading={loading}
      sort={sort}
      onSort={toggleSort}
      empty="Nenhuma conta botsvr encontrada"
      mobileTitle={(a) => <span>{a.accountId} · {a.name || '—'}</span>}
      mobileSubtitle={(a) => `${isOn(a) ? 'ON' : 'OFF'} · Poder ${a.combatPower.toLocaleString('pt-BR')}`}
    />
  );
}

function BotDetail({ acc, on, live, onBack, onChanged }: {
  acc: BotAccountRow; on: boolean; live?: OnlineBotRow; onBack: () => void; onChanged: () => void;
}) {
  const { notify } = useToast();
  const confirm = useConfirm();
  const [startOpen, setStartOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const nation = acc.nation ? NATIONS_PT[acc.nation] : null;
  const worldName = (id?: number | null) => WORLDS.find(w => w.id === id)?.name || (id ? `Mapa ${id}` : '—');

  const start = async (channel: number) => {
    setStartOpen(false);
    setBusy(true);
    try {
      notify('success', (await startOneBot(acc.characterIdx!, channel)).message);
      setTimeout(onChanged, 12000);
    } catch (e) { notify('error', errMsg(e, 'Erro ao ligar o bot')); }
    finally { setBusy(false); }
  };
  const stop = async () => {
    const ok = await confirm({ title: `Desligar ${acc.name}?`, description: 'O bot sai do jogo. Os outros bots continuam.',
                               tone: 'danger', confirmLabel: 'Desligar' });
    if (!ok) return;
    setBusy(true);
    try {
      notify('success', (await stopOneBot(acc.characterIdx!)).message);
      onChanged();
    } catch (e) { notify('error', errMsg(e, 'Erro ao desligar o bot')); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button onClick={onBack} className="flex items-center gap-1 text-xs font-bold text-white/60 hover:text-white cursor-pointer">
          <ChevronLeft size={14} /> Voltar à lista
        </button>
        {on ? (
          <button onClick={stop} disabled={busy}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20 transition cursor-pointer disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Power size={15} />} Desligar
          </button>
        ) : (
          <button onClick={() => setStartOpen(true)} disabled={busy}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 transition cursor-pointer disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} Ligar
          </button>
        )}
      </div>

      {startOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
             onMouseDown={(e) => { if (e.target === e.currentTarget) setStartOpen(false); }}>
          <div className="surface-card rounded-2xl p-5 w-full max-w-md">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-white flex items-center gap-2">
                <Play size={16} className="text-emerald-400" /> Ligar {acc.name} — escolha o canal
              </h3>
              <button onClick={() => setStartOpen(false)} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {CHANNELS.map(c => (
                <button key={c} onClick={() => start(c)}
                  className="px-3 py-2 rounded-lg text-xs font-bold cursor-pointer border transition bg-white/[0.03] text-white/70 border-white/[0.06] hover:text-emerald-400 hover:border-emerald-500/40">
                  Canal {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="surface-card rounded-xl p-4 mb-4 flex flex-wrap items-center gap-x-8 gap-y-3">
        <div>
          <p className="text-lg font-bold text-white">{acc.name}</p>
          <p className="text-xs text-white/50">{acc.accountId} · CharIdx {acc.characterIdx} · {formatClass(acc.charClass)} · Lv {acc.level ?? '—'}</p>
        </div>
        {nation && <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${nation.classes}`}>{nation.name}</span>}
        <div>
          <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold">Status</p>
          <p className={`text-sm font-bold ${on ? 'text-emerald-400' : 'text-white/40'}`}>
            {on ? `ON${live ? ` · CH${live.channel} · ${worldName(live.mapId)} (${live.x},${live.y})` : ''}` : 'OFF'}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold flex items-center gap-1"><Zap size={10} /> Poder de combate</p>
          <p className="text-sm font-bold text-amber-300 font-mono">{acc.combatPower.toLocaleString('pt-BR')}</p>
        </div>
        {on && <p className="text-[11px] text-amber-300/80 w-full">Bot online — para editar itens desligue-o (a edição exige o personagem offline).</p>}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <div className="surface-card rounded-xl p-4">
          <h3 className="text-sm font-bold text-white mb-3">Equipamento</h3>
          <ItemsView source="equipment" id={acc.characterIdx!} classId={acc.charClass ?? undefined} />
        </div>
        <div className="surface-card rounded-xl p-4">
          <h3 className="text-sm font-bold text-white mb-3">Inventário</h3>
          <ItemsView source="inventory" id={acc.characterIdx!} />
        </div>
      </div>

      <PersonalShop characterIdx={acc.characterIdx!} onChanged={onChanged} />
    </div>
  );
}

/* Personal Shop do bot: 8 slots, cada um aponta um item do inventário + preço em alz (tabela _39_BotPersonalShop).
   Abrir a tela já limpa (na API) o slot cujo item saiu do inventário. Abrir a lojinha no jogo = próxima fase. */
const SHOP_SLOTS = 8;
type ShopDraft = { invSlot: number; item: ItemRow | null; price: string; saved: boolean };

function PersonalShop({ characterIdx, onChanged }: { characterIdx: number; onChanged: () => void }) {
  const { notify } = useToast();
  const [slots, setSlots] = useState<(ShopDraft | null)[]>(Array(SHOP_SLOTS).fill(null));
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [removed, setRemoved] = useState(false);   // limpou algum slot já salvo (precisa Salvar)
  // Nome da lojinha: vazio = abre com o padrão da API ("Eu vendo itens.")
  const [title, setTitle] = useState('');
  const [savedTitle, setSavedTitle] = useState('');
  const [defaultTitle, setDefaultTitle] = useState('Eu vendo itens.');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const t = await getBotShopTitle(characterIdx);
      setTitle(t.title); setSavedTitle(t.title); setDefaultTitle(t.defaultTitle);
      const rows = await getBotShop(characterIdx);
      const next: (ShopDraft | null)[] = Array(SHOP_SLOTS).fill(null);
      for (const r of rows) next[r.shopSlot] = { invSlot: r.invSlot, item: r.item, price: String(r.price), saved: true };
      setSlots(next);
    } catch (e) { notify('error', errMsg(e, 'Erro ao carregar a lojinha')); }
    finally { setLoading(false); }
  }, [characterIdx, notify]);
  useEffect(() => { load(); }, [load]);

  const pick = (it: ItemRow) => {
    if (picking == null || it.slot == null) return;
    if (it.bindAccount || it.bindCharacter) {
      notify('error', 'Item vinculado à conta/personagem — não pode ir pra lojinha');
      return;
    }
    const other = slots.findIndex((s, i) => i !== picking && s?.invSlot === it.slot);
    if (other >= 0) { notify('error', `Esse item já está no slot ${other + 1} da lojinha`); return; }
    setSlots(prev => prev.map((s, i) => (i === picking ? { invSlot: it.slot as number, item: it, price: s?.price ?? '', saved: false } : s)));
    setPicking(null);
  };

  const setPrice = (i: number, v: string) =>
    setSlots(prev => prev.map((s, j) => (j === i && s ? { ...s, price: v.replace(/\D/g, '').replace(/^0+(?=\d)/, ''), saved: false } : s)));

  // Salvar GERAL: grava os 8 slots de uma vez (slot vazio = apagado no banco)
  const dirty = removed || slots.some(s => s && !s.saved) || title.trim() !== savedTitle;
  const saveAll = async () => {
    const filled = slots.map((s, i) => (s ? { i, s } : null)).filter(Boolean) as { i: number; s: ShopDraft }[];
    const noPrice = filled.find(({ s }) => !Number(s.price));
    if (noPrice) { notify('error', `Informe o valor em alz do slot ${noPrice.i + 1}`); return; }
    setSaving(true);
    try {
      const rows = await saveBotShop(characterIdx, filled.map(({ i, s }) => ({ shopSlot: i, invSlot: s.invSlot, price: Number(s.price) })),
                                     title.trim());
      setTitle(title.trim()); setSavedTitle(title.trim());
      const next: (ShopDraft | null)[] = Array(SHOP_SLOTS).fill(null);
      for (const r of rows) next[r.shopSlot] = { invSlot: r.invSlot, item: r.item, price: String(r.price), saved: true };
      setSlots(next);
      setRemoved(false);
      notify('success', 'Lojinha salva');
      onChanged();
    } catch (e) { notify('error', errMsg(e, 'Erro ao salvar a lojinha')); }
    finally { setSaving(false); }
  };

  const clearSlot = (i: number) => {
    if (slots[i]?.saved) setRemoved(true);
    setSlots(prev => prev.map((x, j) => (j === i ? null : x)));
  };

  return (
    <div className="surface-card rounded-xl p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-white">Personal Shop</h3>
        <span className="text-[11px] text-white/40">Clique num slot pra escolher o item do inventário</span>
      </div>
      {!loading && (
        <label className="block mb-3 max-w-md">
          <span className="text-[11px] text-white/50">Nome da loja</span>
          <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, 37))} maxLength={37}
                 placeholder={defaultTitle}
                 className="w-full bg-[#0a0e14] border border-white/[0.08] rounded px-2 py-1.5 text-sm text-white outline-none focus:border-[#00e1d7]/40" />
          <span className="text-[10px] text-white/35">Em branco = abre com "{defaultTitle}" · até 37 caracteres</span>
        </label>
      )}
      {loading ? (
        <div className="py-6 flex justify-center text-white/40"><Loader2 size={18} className="animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3">
          {slots.map((s, i) => (
            <div key={i} className="rounded-lg bg-[#080c11] border border-white/[0.06] p-2 flex flex-col items-center gap-2">
              <div className="w-full flex items-center justify-between">
                <span className="text-[10px] font-bold text-white/45">SLOT {i + 1}</span>
                {s && (
                  <button onClick={() => clearSlot(i)} disabled={saving} title="Limpar slot"
                    className="text-white/35 hover:text-red-400 cursor-pointer disabled:opacity-40"><Trash2 size={12} /></button>
                )}
              </div>
              <button onClick={() => setPicking(i)} title={s?.item ? `${s.item.itemName || s.item.itemId} — trocar` : 'Escolher item'}
                className={`relative overflow-hidden w-14 h-14 rounded-md border flex items-center justify-center cursor-pointer transition
                  ${s ? 'border-[#00e1d7]/40 bg-black' : 'border-dashed border-white/15 hover:border-[#00e1d7]/50 bg-black/40'}`}>
                {s?.item ? <SlotIcon itemId={s.item.cleanItemId ?? s.item.itemId} size={52} /> : <Plus size={18} className="text-white/30" />}
              </button>
              <p className="text-[10px] text-white/70 text-center leading-tight h-6 overflow-hidden w-full">
                {s?.item ? <>{(s.item.upgradeLevel || 0) > 0 && <b className="text-amber-300">+{s.item.upgradeLevel} </b>}{s.item.itemName || `Item ${s.item.itemId}`}</> : ''}
              </p>
              {s && (
                <>
                  <input value={s.price} onChange={(e) => setPrice(i, e.target.value)} placeholder="Valor (alz)" inputMode="numeric"
                    className="w-full bg-[#0a0e14] border border-white/[0.08] rounded px-2 py-1 text-xs text-white font-mono text-right outline-none focus:border-[#00e1d7]/40" />
                  <span className="text-[9px] text-white/35 -mt-1">{s.price ? `${Number(s.price).toLocaleString('pt-BR')} alz` : ' '}</span>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Salvar GERAL (os 8 slots de uma vez) */}
      {!loading && (
        <div className="flex items-center justify-end gap-3 mt-4">
          {dirty && <span className="text-[11px] text-amber-300/80">Alterações não salvas</span>}
          <button onClick={saveAll} disabled={saving || !dirty}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#00e1d7]/15 border border-[#00e1d7]/35 text-[#00e1d7] text-sm font-bold hover:bg-[#00e1d7]/25 cursor-pointer disabled:opacity-40">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
          </button>
        </div>
      )}

      {picking != null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
             onMouseDown={(e) => { if (e.target === e.currentTarget) setPicking(null); }}>
          <div className="surface-card rounded-2xl p-5 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold text-white">Item do slot {picking + 1} da lojinha</h3>
              <button onClick={() => setPicking(null)} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
            </div>
            <p className="text-[11px] text-white/45 mb-3">Clique num item do inventário. Item vinculado à conta/personagem não pode.</p>
            <ItemsView source="inventory" id={characterIdx} onPick={pick} />
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({
  active, onClick, children,
}: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer whitespace-nowrap ${
        active
          ? 'bg-[#00e1d7]/15 text-[#00e1d7]'
          : 'text-white/55 hover:text-white hover:bg-white/[0.03]'
      }`}
    >
      {children}
    </button>
  );
}
